import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = {
  key: 0,
  class: "fetching-state"
}
const _hoisted_3 = {
  key: 1,
  class: "error-text"
}
const _hoisted_4 = {
  key: 2,
  class: "info-note"
}

import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import VendorDashboardHeader from "@/modules/vendor/components/VendorDashboardHeader.vue";

import InquiryDetailAttachmentsSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailAttachmentsSection.vue";
import InquiryDetailDescriptionSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailDescriptionSection.vue";
import InquiryDetailDetailsSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailDetailsSection.vue";
import InquiryDetailQuotesSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailQuotesSection.vue";
import InquiryDetailResponsesSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailResponsesSection.vue";

import {
  fetchVendorInquiryDetails,
  type InquiryDetail,
} from "@/shared/services/inquiries.api";
import { formatPhoneNumber } from "@/shared/utils/phone";



export default /*@__PURE__*/_defineComponent({
  __name: 'InquiryDetailPage',
  setup(__props) {

const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const inquiryData = ref<InquiryDetail | null>(null);


function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function formatLocation(source: InquiryDetail | null) {
  const area = source?.service_area;
  const city = asString(area?.city);
  const state = asString(area?.state);
  const zip = asString(area?.zip);
  return [city, state, zip].filter(Boolean).join(", ") || "-";
}

const timelineItems = computed(() => {
  const source = inquiryData.value;
  const timeline = source?.timeline || [];

  if (timeline.length) {
    return timeline.map((item, index) => ({
      title: asString(item.title, `Step ${index + 1}`),
      time: asString(item.time, "-"),
      state: item.state,
    }));
  }

  return [
    { title: "Inquiry Submitted", time: asString(source?.created_at, "-"), state: "done" },
    { title: "Vendors Notified", time: "-", state: "done" },
    { title: "Review In Progress", time: "-", state: "active" },
  ] as const;
});


const quotes = computed(() => {
  const items = inquiryData.value?.quotes || [];

  return items.map((item) => ({
    id: item.id,
    quoteNo: asString(item.quote_no),
    title: asString(item.vendor_name, "Quotation"),
    price: asString(item.price, "-"),
    date: asString(item.date, "-"),
    status: asString(item.status, "Received"),
    icon: "mdi-file-document-outline",
    iconTone: "quote-icon--peach",
  }));
});

const description = computed(() => {
  const source = inquiryData.value;
  const service = source?.service;
  const priority = asString(service?.priority);
  const inquiryType = asString(source?.type).toLowerCase();
  const serviceName =
    inquiryType === "inspection"
      ? "Inspection Request"
      : asString(service?.name, "Inquiry");
  const fallbackDescription = formatLocation(source);

  return {
    title: serviceName,
    time: asString(source?.created_at, "-"),
    inquiryNo: asString(source?.inquiry_no || source?.id, "-"),
    description: asString(
      service?.description,
      fallbackDescription || "No description available.",
    ),
    icon: "mdi-wrench",
    iconTone: "card-icon--peach",
    priority,
  };
});

const details = computed(() => {
  const source = inquiryData.value;
  const service = source?.service;
  const client = source?.client;
  const area = source?.service_area;
  const addressLine = asString(area?.address_line);
  const city = asString(area?.city);
  const state = asString(area?.state);
  const zip = asString(area?.zip);
  const locationParts = [addressLine, city, state, zip].filter(Boolean);
  const contactName = asString(client?.name);
  const contactPhoneRaw = asString(client?.phone);
  const contactPhone = contactPhoneRaw
    ? formatPhoneNumber(contactPhoneRaw)
    : "";
  const contactPerson = [contactName, contactPhone].filter(Boolean).join(" • ");

  return [
    {
      icon: "mdi-account-outline",
      label: "Name",
      value: contactName || "-",
    },
    {
      icon: "mdi-email-outline",
      label: "Email",
      value: asString(client?.email, "-"),
    },
    {
      icon: "mdi-phone-outline",
      label: "Phone",
      value: contactPhone || "-",
    },
    {
      icon: "mdi-map-marker-outline",
      label: "Service Address",
      value: locationParts.join(", ") || "-",
    },
    {
      icon: "mdi-clock-outline",
      label: "Submitted",
      value: asString(source?.created_at, "-"),
    },
  ];
});

const attachments = computed(() => {
  const items = inquiryData.value?.attachments || [];

  return items.map((item) => ({
    url: asString(item.url),
    description: asString(item.description, "Attachment"),
  }));
});

const responses = computed(() => {
  const items = inquiryData.value?.responses || [];

  return items.map((item) => ({
    label: asString(item.label, "Response"),
    value: asString(item.value, "-"),
  }));
});

async function loadInquiryDetail() {
  const id = asString(route.params.id).trim();
  if (!id) {
    errorMessage.value = "Inquiry not found.";
    inquiryData.value = null;
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response = await fetchVendorInquiryDetails(id);
    inquiryData.value = response || null;
    if (!inquiryData.value) {
      errorMessage.value = "Inquiry details are unavailable.";
    }
  } catch (error) {
    inquiryData.value = null;
    errorMessage.value = "Unable to load inquiry details.";
    console.error("Failed to load inquiry details", error);
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  () => {
    loadInquiryDetail();
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(VendorDashboardHeader, {
      title: "",
      subtitle: ""
    }),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(LoadingState, {
            loading: isLoading.value,
            message: "Loading inquiry details...",
            "spinner-size": 24,
            "spinner-width": 3
          }, null, 8, ["loading"])
        ]))
      : (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errorMessage.value), 1))
        : (!inquiryData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                size: "18",
                class: "info-note-icon"
              }, {
                default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                  _createTextVNode("mdi-information-outline", -1)
                ]))]),
                _: 1
              }),
              _cache[1] || (_cache[1] = _createElementVNode("p", { class: "info-note-text" }, "Inquiry details are unavailable.", -1))
            ]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createVNode(InquiryDetailDescriptionSection, {
                title: description.value.title,
                time: description.value.time,
                "inquiry-no": description.value.inquiryNo,
                description: description.value.description,
                icon: description.value.icon,
                "icon-tone": description.value.iconTone,
                priority: description.value.priority
              }, null, 8, ["title", "time", "inquiry-no", "description", "icon", "icon-tone", "priority"]),
              _createVNode(InquiryDetailQuotesSection, {
                quotes: quotes.value,
                "inquiry-id": inquiryData.value?.id
              }, null, 8, ["quotes", "inquiry-id"]),
              _createVNode(InquiryDetailDetailsSection, { items: details.value }, null, 8, ["items"]),
              (attachments.value.length)
                ? (_openBlock(), _createBlock(InquiryDetailAttachmentsSection, {
                    key: 0,
                    items: attachments.value
                  }, null, 8, ["items"]))
                : _createCommentVNode("", true),
              (responses.value.length && inquiryData.value?.type !== 'inspection')
                ? (_openBlock(), _createBlock(InquiryDetailResponsesSection, {
                    key: 1,
                    items: responses.value
                  }, null, 8, ["items"]))
                : _createCommentVNode("", true)
            ], 64))
  ]))
}
}

})